import React, { useEffect, useState, useRef } from 'react';
import usePersistedState from '../utils/useListPreferences';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import { Search, Lock, ChevronRight, ChevronLeft, Star, Filter, CheckCircle2 } from 'lucide-react';
import secureStorage from '../utils/secureStorage';
import { API_BASE } from '../config/api';
import DifficultyBadge from './DifficultyBadge';
import ForecastStars, { ForecastLegend } from './ForecastStars';

const ReadingForecast = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [liveCounts, setLiveCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [isVIP, setIsVIP] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('default');
  // Remembered so returning from a part lands back on the page it was on.
  const [currentPage, setCurrentPage] = usePersistedState('listPage:readingForecast', 1);
  const [hideDone, setHideDone] = usePersistedState('hideDone:readingForecast', false, 'local');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedPart, setSelectedPart] = useState('all');
  const itemsPerPage = 6;

  const TYPE_LABELS = {
    'true_false_ng': 'True/False/NG',
    'yes_no_ng': 'Yes/No/NG',
    'fill_blank': 'Fill in Blank',
    'fill_in_blank': 'Fill in Blank',
    'multiple_choice': 'Multiple Choice (Single Answer)',
    'checkbox': 'Multiple Choice (Multiple Answers)',
    'matching': 'Matching Information',
    'matching_headings': 'Matching Headings',
    'matching_features_drag': 'Matching Features (Drag-and-drop)',
    'matching_features_table': 'Matching Features (Table)',
    'matching_sentence_endings': 'Matching Sentence Endings',
    'sentence_completion': 'Sentence Completion',
    'summary_completion': 'Summary Completion',
    'summary_completion_word_list': 'Summary Completion (With Word List)',
    'short_answer': 'Short Answer',
    'diagram_labelling': 'Diagram Labelling',
    'map_labelling': 'Map Labelling',
    'note_completion': 'Note Completion',
    'table_completion': 'Table Completion',
    'flow_chart': 'Flow Chart',
  };
  const [userRole, setUserRole] = useState('');
  const [examHistoryDropdowns, setExamHistoryDropdowns] = useState({});
  const [examHistories, setExamHistories] = useState({});
  const [loadingHistory, setLoadingHistory] = useState({});
  const examHistoryRefs = useRef({});

  useEffect(() => {
    const fetchUserRole = async () => {
      let token = secureStorage.getItem('token') || localStorage.getItem('token');
      let userId = secureStorage.getItem('user_id') || localStorage.getItem('user_id');
      if (!token || !userId) { navigate('/login'); return; }
      try {
        const res = await fetch(`${API_BASE}/student/user-role/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) { const data = await res.json(); setUserRole(data.role); } else if (res.status === 401) { navigate('/login'); }
      } catch (_) { }
    };
    fetchUserRole();
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      const token = secureStorage.getItem('token') || localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }
      try {
        const [forecastRes, vipRes] = await Promise.all([
          fetch(`${API_BASE}/student/reading/forecasts`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_BASE}/customer/vip/subscription/status`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        const [forecastData, vipData] = await Promise.all([forecastRes.json(), vipRes.json()]);
        // Use the new skill-specific access flag (supports multiple subscriptions)
        const hasAccess = vipData.has_reading_access || false;
        setIsVIP(hasAccess);
        const flat = [];
        (Array.isArray(forecastData) ? forecastData : []).forEach(exam => {
          (exam.parts || []).forEach(p => flat.push({
            exam_id: exam.exam_id,
            exam_title: exam.exam_title,
            part_number: p.part_number,
            forecast_title: p.forecast_title || '',
            difficulty_label: p.difficulty_label || null,
            difficulty_score: p.difficulty_score,
            forecast_level: p.forecast_level || null,
            occurrence_count: p.occurrence_count || 0,
            attempts_count: p.attempts_count || 0,
            latest_score: p.latest_score,
            total_questions: p.total_questions,
            completed: !!p.completed,
            is_recommended: !!p.is_recommended,
            question_types: p.question_types || []
          }));
        });
        setItems(flat);
      } catch (_) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  // Live "người đang làm bài" per forecast part (scope = "<exam>p<part>"), refreshed periodically.
  useEffect(() => {
    if (!items.length) return;
    const token = secureStorage.getItem('token') || localStorage.getItem('token');
    const scopes = items.map(it => `${it.exam_id}p${it.part_number}`).join(',');
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const res = await fetch(`${API_BASE}/student/live-presence?exam_ids=${scopes}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setLiveCounts(data.counts || {});
        }
      } catch (e) { /* best-effort */ }
    };
    fetchCounts();
    const iv = setInterval(fetchCounts, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [items]);

  // Extract all unique question types
  const allTypes = [...new Set(items.flatMap(it => it.question_types || []))].filter(Boolean);

  const isCustomer = (userRole || '').toLowerCase() === 'customer';
  const isLimitedUser = isCustomer && !isVIP;
  const canSearch = !isLimitedUser;
  // "Ẩn bài đã làm" rides on the same VIP gate as search/sort here.
  const canHideDone = !isLimitedUser;
  const keepItem = (it) => !(hideDone && canHideDone) || !(it.completed);

  const filtered = (canSearch
    ? items.filter(keepItem).filter(it => {
      const matchSearch = ((it.exam_title || '').toLowerCase().includes(searchQuery.toLowerCase())) || ((it.forecast_title || '').toLowerCase().includes(searchQuery.toLowerCase()));
      const matchType = selectedType === 'all' || (it.question_types || []).includes(selectedType);
      const matchPart = selectedPart === 'all' || it.part_number === selectedPart;
      return matchSearch && matchType && matchPart;
    }).sort((a, b) => {
      if (sortOrder === 'alphabet_asc') {
        const titleA = a.forecast_title || a.exam_title || '';
        const titleB = b.forecast_title || b.exam_title || '';
        return titleA.localeCompare(titleB);
      } else if (sortOrder === 'alphabet_desc') {
        const titleA = a.forecast_title || a.exam_title || '';
        const titleB = b.forecast_title || b.exam_title || '';
        return titleB.localeCompare(titleA);
      } else if (sortOrder === 'difficulty') {
        // Easy first = higher avg %; unclassified parts go last.
        const av = a.difficulty_score, bv = b.difficulty_score;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return bv - av;
      } else if (sortOrder === 'forecast') {
        // Trúng tủ cao trước = occurrence_count desc.
        return (b.occurrence_count || 0) - (a.occurrence_count || 0);
      }
      return 0; // default order
    })
    : items);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginated = filtered.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;

  // A remembered page can point past the end after hiding finished items. Only clamp
  // once the list has actually loaded — on the first render `items` is still empty and
  // totalPages falls back to 1, which would throw away the remembered page.
  useEffect(() => {
    if (items.length > 0 && totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [items.length, totalPages, currentPage, setCurrentPage]);

  const calculateBandScore = (totalScore) => {
    const bandScore = (totalScore / 40) * 9;
    return Math.round(bandScore * 2) / 2;
  };

  const fetchExamHistory = async (examId, partNumber) => {
    const key = `${examId}-${partNumber}`;
    if (examHistories[key]) {
      return;
    }
    setLoadingHistory(prev => ({ ...prev, [key]: true }));
    try {
      const token = secureStorage.getItem('token') || localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/student/reading/forecast-history/${examId}/${partNumber}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const results = await response.json();
        setExamHistories(prev => ({ ...prev, [key]: Array.isArray(results) ? results : [] }));
      } else {
        setExamHistories(prev => ({ ...prev, [key]: [] }));
      }
    } catch (_) {
      setExamHistories(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingHistory(prev => ({ ...prev, [key]: false }));
    }
  };

  const toggleExamHistoryDropdown = (examId, partNumber) => {
    const key = `${examId}-${partNumber}`;
    const isOpen = !!examHistoryDropdowns[key];
    setExamHistoryDropdowns({});
    if (!isOpen) {
      setExamHistoryDropdowns({ [key]: true });
      fetchExamHistory(examId, partNumber);
    }
  };

  const handleViewPreviousExam = (examId, resultId, partNumber) => {
    navigate('/reading_test_room', {
      state: {
        examId: examId,
        resultId: resultId,
        fromResultReview: true,
        forecastPart: partNumber
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2">
            <li>
              <Link to="/" className="text-gray-500 hover:text-[#0096b1]">Home</Link>
            </li>
            <li><span className="text-gray-400 mx-2">/</span></li>
            <li><span className="text-[#0096b1] font-medium">Reading Forecast Parts</span></li>
          </ol>
        </nav>
        <ForecastLegend />
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={isLimitedUser ? "Tìm kiếm chỉ dành cho VIP..." : "Tìm kiếm bài dự đoán..."}
              className={`w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0096b1] focus:border-[#0096b1] ${isLimitedUser ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              value={searchQuery}
              onChange={(e) => canSearch && setSearchQuery(e.target.value)}
              disabled={isLimitedUser}
            />
            {isLimitedUser && (
              <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            )}
          </div>
          <select
            className={`px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0096b1] focus:border-[#0096b1] font-medium ${isLimitedUser ? 'bg-gray-100 cursor-not-allowed text-gray-400' : 'bg-white text-gray-700'}`}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            disabled={isLimitedUser}
            title={isLimitedUser ? 'Sắp xếp chỉ dành cho VIP' : undefined}
          >
            <option value="default">Mới nhất</option>
            <option value="forecast">Theo trúng tủ (cao trước)</option>
            <option value="difficulty">Theo độ khó (dễ trước)</option>
            <option value="alphabet_asc">Theo Alphabet (A-Z)</option>
            <option value="alphabet_desc">Theo Alphabet (Z-A)</option>
          </select>
          <select
            className={`px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0096b1] focus:border-[#0096b1] font-medium ${isLimitedUser ? 'bg-gray-100 cursor-not-allowed text-gray-400' : 'bg-white text-gray-700'}`}
            value={selectedPart}
            onChange={(e) => { setSelectedPart(e.target.value === 'all' ? 'all' : Number(e.target.value)); setCurrentPage(1); }}
            disabled={isLimitedUser}
            title={isLimitedUser ? 'Lọc chỉ dành cho VIP' : undefined}
          >
            <option value="all">Tất cả part</option>
            {[...new Set(items.map(it => it.part_number).filter(Boolean))].sort((a, b) => a - b).map(pn => (
              <option key={pn} value={pn}>Part {pn}</option>
            ))}
          </select>
          {canHideDone && (
            <label className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg cursor-pointer select-none bg-white hover:bg-gray-50 whitespace-nowrap">
              <input
                type="checkbox"
                className="w-4 h-4 accent-[#0096b1] cursor-pointer"
                checked={hideDone}
                onChange={(e) => { setHideDone(e.target.checked); setCurrentPage(1); }}
              />
              <span className="text-sm font-medium text-gray-700">Ẩn bài đã làm</span>
            </label>
          )}
        </div>   {/* Question Type Filter */}
        {allTypes.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-600">Lọc theo dạng câu hỏi:</span>
              {isLimitedUser && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                  <Lock className="w-3 h-3" />
                  Chỉ dành cho VIP
                </span>
              )}
            </div>
            <div className={`flex flex-wrap gap-2 ${isLimitedUser ? 'opacity-50 pointer-events-none select-none' : ''}`}>
              <button
                onClick={() => { setSelectedType('all'); setCurrentPage(1); }}
                disabled={isLimitedUser}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${selectedType === 'all'
                  ? 'bg-[#0096b1] text-white border-[#0096b1]'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-[#0096b1] hover:text-[#0096b1]'
                  }`}
              >
                Tất cả ({items.length})
              </button>
              {allTypes.map(type => {
                const count = items.filter(it => (it.question_types || []).includes(type)).length;
                return (
                  <button
                    key={type}
                    onClick={() => { setSelectedType(type); setCurrentPage(1); }}
                    disabled={isLimitedUser}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${selectedType === type
                      ? 'bg-[#0096b1] text-white border-[#0096b1]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#0096b1] hover:text-[#0096b1]'
                      }`}
                  >
                    {TYPE_LABELS[type] || type} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center text-gray-600">Đang tải...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-600">Chưa có bài kiểm tra dự đoán</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginated.map((it, index) => (
                <div key={`${it.exam_id}-${it.part_number}`} className="bg-white rounded-lg shadow border border-gray-100 p-4 relative">
                  {it.is_recommended && (
                    <div className="absolute top-2 right-2">
                      <Star className="w-5 h-5 text-yellow-500" fill="currentColor" />
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-3 pr-6">
                    <h3 className="text-lg font-semibold text-gray-800">
                      <span className="text-[#0096b1] font-medium mr-2">Test gốc:</span>
                      <span className="truncate max-w-[70%] inline-block align-bottom" title={it.exam_title}>{it.exam_title}</span>
                    </h3>
                    {(() => {
                      const hasHistory = it.attempts_count > 0;
                      const canShowHistory = hasHistory && (!isLimitedUser || (index + indexOfFirstItem) < 6);
                      if (!canShowHistory) return null;
                      return (
                        <div className="relative" ref={el => examHistoryRefs.current[`${it.exam_id}-${it.part_number}`] = el}>
                          <button
                            onClick={() => toggleExamHistoryDropdown(it.exam_id, it.part_number)}
                            className="flex items-center space-x-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                          >
                            Lịch sử
                            <ChevronRight className={`w-4 h-4 transition-transform ${examHistoryDropdowns[`${it.exam_id}-${it.part_number}`] ? 'rotate-90' : ''}`} />
                          </button>
                          {examHistoryDropdowns[`${it.exam_id}-${it.part_number}`] && (
                            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                              <div className="p-3 border-b border-gray-100">
                                <h4 className="font-medium text-gray-900">History</h4>
                              </div>
                              <div className="max-h-48 overflow-y-auto">
                                {loadingHistory[`${it.exam_id}-${it.part_number}`] ? (
                                  <div className="p-4 text-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                                    <p className="text-sm text-gray-500 mt-2">Loading...</p>
                                  </div>
                                ) : examHistories[`${it.exam_id}-${it.part_number}`] && examHistories[`${it.exam_id}-${it.part_number}`].length > 0 ? (
                                  <>
                                    {examHistories[`${it.exam_id}-${it.part_number}`].slice(0, 2).map((result) => (
                                      <button
                                        key={result.result_id}
                                        onClick={() => handleViewPreviousExam(it.exam_id, result.result_id, it.part_number)}
                                        className="w-full p-3 text-left hover:bg-gray-50 border-b border-gray-100 transition-colors"
                                      >
                                        <div className="flex justify-between items-start">
                                          <div>
                                            <p className="text-sm font-medium text-gray-900">Attempt #{result.attempt_number || ''}</p>
                                            <p className="text-xs text-gray-500">
                                              {new Date(result.completion_date).toLocaleString('vi-VN', {
                                                timeZone: 'Asia/Ho_Chi_Minh',
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit'
                                              })}
                                            </p>
                                          </div>
                                          <div className="text-right">
                                            <p className="text-sm font-medium text-blue-600">{(result.total_score ?? result.score_earned ?? 0)}/{(result.score_total ?? 40)}</p>
                                          </div>
                                        </div>
                                      </button>
                                    ))}
                                  </>
                                ) : (
                                  <div className="p-4 text-center text-gray-500">
                                    <p className="text-sm">No history</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="mt-2 text-md text-gray-700">
                    <span>Dự đoán Part: </span>
                    <span className={`${(isLimitedUser && (index + indexOfFirstItem) >= 6) ? 'blur-[4px] select-none' : ''}`}>
                      {it.part_number}{it.forecast_title ? ` – ${it.forecast_title}` : ''}
                    </span>
                    <DifficultyBadge label={it.difficulty_label} className="ml-2 align-middle" />
                    <ForecastStars level={it.forecast_level} className="ml-2" />
                  </div>
                  {it.latest_score != null && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#eb7e37]">
                      <CheckCircle2 className="w-4 h-4" /> Đúng {it.latest_score}/{it.total_questions}
                    </div>
                  )}
                  {liveCounts[`${it.exam_id}p${it.part_number}`] > 0 && (
                    <div className="mt-2 flex items-center gap-1.5 text-[#0096b1] font-semibold text-sm">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0096b1] opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0096b1]"></span>
                      </span>
                      {liveCounts[`${it.exam_id}p${it.part_number}`]} người đang làm bài
                    </div>
                  )}
                  {!(isLimitedUser && (index + indexOfFirstItem) >= 6) && (it.question_types || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(it.question_types || []).map(type => (
                        <span
                          key={type}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0096b1]/10 text-[#0096b1] border border-[#0096b1]/20"
                        >
                          {TYPE_LABELS[type] || type}
                        </span>
                      ))}
                    </div>
                  )}
                  {(isLimitedUser && (index + indexOfFirstItem) >= 6) ? (
                    <div className="mt-4 p-3 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-gray-700">
                        <Lock className="w-5 h-5 text-[#0096b1]" />
                        <span className="text-sm font-medium">VIP cần nâng cấp để truy cập</span>
                      </div>
                      <Link
                        to="/vip-packages?type=reading"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#0096b1] text-white hover:bg-[#00839a] text-sm"
                      >
                        Xem gói
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        // Clear highlights and notes from previous attempt
                        localStorage.removeItem('ielts-highlights');
                        localStorage.removeItem('ielts-notes');
                        navigate('/reading_test_room', { state: { examId: it.exam_id, forecastPart: it.part_number } });
                      }}
                      className={`mt-4 w-full py-2 rounded text-white ${it.attempts_count > 0 ? 'bg-orange-600 hover:bg-orange-700' : 'bg-[#0096b1] hover:bg-[#00839a]'}`}
                    >
                      {it.attempts_count > 0 ? 'Làm lại' : 'Làm bài'}
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-center items-center space-x-4 mt-8">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={3} />
              </button>
              <span className="text-gray-600 font-bold">
                Trang {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" strokeWidth={3} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReadingForecast;
